import { useCallback, useMemo, useSyncExternalStore } from 'react'

import {
  type BreakpointValue,
  isDirection,
  mediaBetween,
  mediaDown,
  mediaUp,
} from './media-query-helpers'

export function useMediaQuery(direction: 'up' | 'down', breakpoint: BreakpointValue): boolean
export function useMediaQuery(
  direction: 'between',
  start: BreakpointValue,
  end: BreakpointValue,
): boolean
export function useMediaQuery(query: string): boolean
export function useMediaQuery(
  directionOrQuery: string,
  startOrBreakpoint?: BreakpointValue,
  end?: BreakpointValue,
): boolean {
  const query = useMemo(() => {
    if (isDirection(directionOrQuery)) {
      if (startOrBreakpoint === undefined) {
        throw new Error(`useMediaQuery: '${directionOrQuery}' requires a breakpoint argument`)
      }

      if (directionOrQuery === 'between') {
        if (end === undefined) {
          throw new Error("useMediaQuery: 'between' requires start and end breakpoints")
        }

        return mediaBetween(startOrBreakpoint, end)
      }

      return directionOrQuery === 'down' ? mediaDown(startOrBreakpoint) : mediaUp(startOrBreakpoint)
    }

    return directionOrQuery.replace(/^@media\s+/, '')
  }, [directionOrQuery, startOrBreakpoint, end])

  const getMediaQueryList = useCallback(
    () =>
      typeof window === 'undefined' || typeof window.matchMedia !== 'function'
        ? undefined
        : window.matchMedia(query),
    [query],
  )

  const subscribe = useCallback(
    (callback: () => void) => {
      const mql = getMediaQueryList()
      if (mql === undefined) {
        return () => undefined
      }

      mql.addEventListener('change', callback)

      return () => mql.removeEventListener('change', callback)
    },
    [getMediaQueryList],
  )

  const getSnapshot = useCallback(() => getMediaQueryList()?.matches ?? false, [getMediaQueryList])

  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
